import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener
} from '@angular/core';
import { filter, map, take, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TixAnalyticsService } from '@tix/shared/analytics';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { UserPartialState, UserSelector } from '@tix/auth/state';
import { TixCompany, TixUserRole } from '@tix/data-access';
import { ROLE_TYPES } from 'libs/auth/state/src/lib/+state/user.model';
import { ReleaseRedirectService } from '@tix/auth/services';
import { Log, LogsState, NetworkCall } from '../../state/log.state';
import { selectLogs, selectNetworkCalls } from '../../state/log.selectors';
import {
  FeedbackService,
  TixFeedbackCategory
} from '../../services/feedback/feedback.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { User } from '@tix/shared/models';
import * as CompanySelectors from '@tix/company/state/selectors';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { SharedState } from '../../state/shared/shared.reducers';
import { toggleSubmitFeedbackPopover } from '../../state/shared/shared.actions';
import { selectSubmitFeedback } from '../../state/shared/shared.selectors';

import { CompanyAccessService } from '@tix/company/state';
import { AuthStorage } from '@tix/auth/services';
import { selectCompany } from 'libs/company/state/src/lib/+state/actions/company.actions';
import { TixUserPreferencesService } from '@tix/shared/state';
import { USER_PREFERENCE_KEYS } from '@tix/shared/state';
import { SidebarStateService } from '../../services/sidebar-state.service';

export enum FEEDBACK_CATEGORY {
  BUG_REPORT = 'Bug Report',
  FEATURE_REQUEST = 'Feature Request',
  SUGGESTED_IMPROVEMENT = 'Suggested Improvement',
  OTHER = 'Other'
}

@Component({
  selector: 'tix-company-layout',
  templateUrl: './company-layout.component.html',
  styleUrls: ['./company-layout.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('closed', style({ width: '56px', overflow: 'hidden' })),
      state('open', style({ width: '240px' })),
      transition('closed <=> open', [animate('300ms ease-in-out')])
    ])
  ],
  providers: [CompanyAccessService]
})
export class TixCompanyLayoutComponent implements OnInit, OnDestroy {
  readonly userRoles$: Observable<any> = this.store.select(
    UserSelector.getUserRolesInfo
  );
  readonly destroyed$ = new Subject();

  readonly authenticatedUser$: Observable<User | null> = this.store.select(
    UserSelector.getAuthenticatedUser
  );

  readonly selectedCompany$: Observable<TixCompany | undefined> =
    this.store.select(CompanySelectors.getSelectedCompany);

  feedbackForm: FormGroup;

  feedbackCategories = Object.entries(FEEDBACK_CATEGORY).map(
    ([key, value]) => ({
      value: key,
      label: value
    })
  );

  readonly logs$: Observable<Log[]> = this.store.select(selectLogs);
  readonly networks$: Observable<NetworkCall[]> =
    this.store.select(selectNetworkCalls);

  userRoles: TixUserRole[] = [];
  authenticatedUser: User | null;
  selectedCompany: TixCompany | undefined;
  companyId = '';

  selectedFiles: { file: File; preview: string; name: string }[] = [];
  expandSidebar = true;
  showMobileSidebar = false;

  isLoading = true;
  isCompanyReady = false;
  loadingError: string | null = null;

  isSubmitFeedbackOpen: boolean;

  isSubmitting: boolean = false;
  submittingError: boolean = false;

  isMoreThanOneVideoSelected: boolean = false;
  isVideoSizeExceeded: boolean = false;

  feedbackFormSubmited = false;
  showPopup = false;

  get companyName(): string {
    return this.selectedCompany?.name!;
  }

  get userId(): string {
    return this.authenticatedUser?.uid!;
  }
  get userEmail(): string {
    return this.authenticatedUser?.email!;
  }

  constructor(
    private router: Router,
    private analytics: TixAnalyticsService,
    private readonly store: Store,
    private readonly sharedStore: Store<SharedState>,
    private releaseService: ReleaseRedirectService,
    private companyAccessService: CompanyAccessService,
    private feedbackService: FeedbackService,
    private userPreferencesService: TixUserPreferencesService,
    private sidebarStateService: SidebarStateService
  ) {}

  async ngOnInit() {
    try {
      this.store.select(selectSubmitFeedback).subscribe({
        next: isVisible => (this.isSubmitFeedbackOpen = isVisible)
      });

      this.companyId = this.router.url.split('/')[2];

      if (!this.companyId) {
        this.loadingError = 'Invalid company ID';
        this.isLoading = false;
        return;
      }

      // Initialize sidebar state based on screen size
      this.initializeSidebarState();

      this.isLoading = true;
      this.isCompanyReady = false;
      this.loadingError = null;

      // Step 1: Switch to company context
      await this.companyAccessService.switchToCompany(this.companyId);

      // Step 2: Check for releases and handle redirects
      await this.checkReleaseAndRedirect();

      // Step 3: Load company data
      this.store.dispatch(selectCompany({ companyId: this.companyId }));

      // Step 4: Wait for essential data to be available
      await this.waitForEssentialData();

      // Step 5: Setup subscriptions
      this.setupSubscriptions();

      // Step 6: Initialize feedback form
      this.initializeFeedbackForm();

      // Mark as ready
      this.isCompanyReady = true;
      this.isLoading = false;
    } catch (error) {
      console.error('Error during company layout initialization:', error);
      this.loadingError =
        'Failed to load company data. Please try refreshing the page.';
      this.isLoading = false;
      this.isCompanyReady = false;
    }
  }

  private initializeSidebarState(): void {
    if (this.isMobileView()) {
      this.showMobileSidebar = false;
      this.expandSidebar = true; // Default to expanded when it shows
    } else {
      this.showMobileSidebar = false;
      // Load saved sidebar state for desktop, default to true if not found
      const savedSidebarState = this.userPreferencesService.getPreference(
        USER_PREFERENCE_KEYS.SIDEBAR_EXPANDED
      );
      this.expandSidebar = savedSidebarState
        ? savedSidebarState === 'true'
        : true;
    }

    // Initialize the sidebar state service
    this.sidebarStateService.setExpanded(this.expandSidebar);
  }

  private async waitForEssentialData(): Promise<void> {
    // Wait for company data to be loaded
    return new Promise(resolve => {
      const subscription = this.selectedCompany$
        .pipe(
          filter(company => !!company),
          take(1)
        )
        .subscribe({
          next: () => {
            resolve();
            subscription.unsubscribe();
          }
        });

      // Timeout after 10 seconds
      setTimeout(() => {
        subscription.unsubscribe();
        resolve();
      }, 10000);
    });
  }

  private setupSubscriptions(): void {
    this.userRoles$.pipe(takeUntil(this.destroyed$)).subscribe(userRoles => {
      this.userRoles = userRoles?.userRoles;
    });

    this.authenticatedUser$.pipe(takeUntil(this.destroyed$)).subscribe(user => {
      this.authenticatedUser = user;
    });

    this.selectedCompany$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(company => {
        this.selectedCompany = company;
      });
  }

  private initializeFeedbackForm(): void {
    this.feedbackForm = new FormGroup({
      description: new FormControl('', [Validators.required]),
      category: new FormControl('', [Validators.required]),
      title: new FormControl('', [Validators.required]),
      attachments: new FormControl(null)
    });
  }

  ngOnDestroy(): void {
    this.selectedFiles.forEach(file => {
      if (file.preview.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
    });

    this.destroyed$.next(true);
    this.destroyed$.complete();

    AuthStorage.setItem('role', 'user');

    this.selectedFiles.forEach(file => {
      if (file.preview.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
    });
  }

  closeSubmitFeedbackPopover(): void {
    this.sharedStore.dispatch(toggleSubmitFeedbackPopover());
  }

  async checkReleaseAndRedirect() {
    const { willRedirect } = await this.releaseService
      .checkAndRedirect(this.companyId)
      .toPromise();

    // We will keep the app in a loading state until the redirection happens
    if (willRedirect) {
      // Don't continue loading if we're redirecting
      throw new Error('Redirecting to release page');
    }
  }

  togglePopup(): void {
    this.showPopup = !this.showPopup;
  }

  get categoryControl() {
    return this.feedbackForm.get('category');
  }
  get titleControl() {
    return this.feedbackForm.get('title');
  }
  get descriptionControl() {
    return this.feedbackForm.get('description');
  }

  async submitFeedback(): Promise<void> {
    this.isSubmitting = true;

    if (this.feedbackForm.valid) {
      const formValue = this.feedbackForm.value;
      const attachments = formValue.attachments;
      const title: string = formValue.title;
      const description: string = formValue.description;
      const category: string = formValue.category;

      const additionalInfos = {
        companyName: this.companyName,
        userId: this.userId,
        userEmail: this.userEmail
      };

      const feedback = {
        title,
        description,
        category: category as TixFeedbackCategory
      };

      let logFile = null;
      let networkFile = null;

      if (category === 'BUG_REPORT') {
        const logs = await this.logs$.pipe(take(1)).toPromise();
        const networkLogs = await this.networks$.pipe(take(1)).toPromise();

        logFile = this.createConsoleLogTextFile(logs);
        networkFile = this.createNetworkLogTextFile(networkLogs);
      }

      const feedbackAttachments = await this.uploadAttachments(attachments);

      this.feedbackService
        .submitFeedback(
          feedbackAttachments,
          feedback,
          additionalInfos,
          logFile,
          networkFile
        )
        .pipe(tap(console.log))
        .subscribe(
          event => {
            if (event.type === HttpEventType.Response) {
              if (event.status >= 200 && event.status < 300) {
                if (event.body?.data?.submitFeedback) {
                  this.feedbackFormSubmited = true;
                  this.feedbackForm.reset();
                } else {
                  this.submittingError = true;
                  this.isSubmitting = false;
                }
              }
            }
          },
          err => {
            console.log('error while upload: ', err);
            this.isSubmitting = false;
            this.submittingError = true;
          }
        );
    }
  }

  async uploadAttachments(attachments: File[]) {
    if (attachments && attachments.length > 0) {
      const uploadedFiles = [];

      for (const attachment of attachments) {
        try {
          const uploadedVideoLink = await this.feedbackService
            .uploadFeedbackVideo(attachment)
            .toPromise();

          if (uploadedVideoLink) {
            uploadedFiles.push({
              url: uploadedVideoLink,
              filename: attachment.name
            });
          }
        } catch (error) {
          console.error(`Error uploading file: ${attachment.name}`, error);
        }
      }

      return uploadedFiles;
    }
    return null;
  }

  videoCount = 0;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    this.isMoreThanOneVideoSelected = false;
    this.isVideoSizeExceeded = false;

    if (files && files.length > 0) {
      for (const file of Array.from(files)) {
        const isDuplicate = this.selectedFiles.some(
          selectedFile =>
            selectedFile.file.name === file.name &&
            selectedFile.file.size === file.size
        );

        if (isDuplicate) {
          continue;
        }

        if (file.type.startsWith('video/')) {
          this.videoCount++;
          if (file.size > 10 * 1024 * 1024) {
            this.isVideoSizeExceeded = true;
          }
          const previewUrl = URL.createObjectURL(file);
          this.selectedFiles.push({
            file,
            preview: previewUrl,
            name: file.name
          });
        } else {
          const reader = new FileReader();
          reader.onload = (e: ProgressEvent<FileReader>) => {
            this.selectedFiles.push({
              file,
              preview: e.target?.result as string,
              name: file.name
            });
          };
          reader.readAsDataURL(file);
        }
      }

      if (this.videoCount > 1) this.isMoreThanOneVideoSelected = true;
      this.feedbackForm.controls['attachments'].patchValue(Array.from(files));
      this.feedbackForm.updateValueAndValidity();
    }
  }

  isImage(file: { file: File; preview: string }): boolean {
    return file.file.type.startsWith('image/');
  }

  removeFile(index: number): void {
    if (this.selectedFiles[index].file.type.startsWith('video/')) {
      this.videoCount--;
      if (this.videoCount <= 1) this.isMoreThanOneVideoSelected = false;
      if (this.selectedFiles[index].file.size > 10 * 1024 * 1024) {
        this.isVideoSizeExceeded = false;
      }
    }
    this.selectedFiles.splice(index, 1);
  }

  createNetworkLogTextFile(logs: NetworkCall[]): File {
    const logFileName = `network-logs-${new Date().toISOString()}.txt`;
    const logText = logs
      .map(log => {
        const formattedBody = log.body ? JSON.stringify(log.body) : null;
        return `[${log.timestamp.toISOString()}] | ${log.method} | ${
          log.url
        } | [${log.status}] ${log.duration}ms | body: ${formattedBody}`;
      })
      .join('\n');

    const blob = new Blob([logText], { type: 'text/plain' });
    const file = new File([blob], logFileName, { type: 'text/plain' });

    return file;
  }

  createConsoleLogTextFile(logs: Log[]): File {
    const logFileName = `console-logs-${new Date().toISOString()}.txt`;

    const logText = logs
      .map(log => {
        const formattedMessage = log.message
          .map(msg => (typeof msg === 'object' ? JSON.stringify(msg) : msg))
          .join(' ');

        return `[${log.timestamp.toISOString()}] [${
          log.type
        }] ${formattedMessage}`;
      })
      .join('\n');

    const blob = new Blob([logText], { type: 'text/plain' });
    const file = new File([blob], logFileName, { type: 'text/plain' });

    return file;
  }

  newFeedback() {
    this.feedbackFormSubmited = false;
    this.isSubmitting = false;
    this.selectedFiles = [];
  }

  toggleSidebar(event: boolean) {
    this.expandSidebar = event;

    // Update the sidebar state service
    this.sidebarStateService.setExpanded(event);

    // Save sidebar state only for desktop users
    if (!this.isMobileView()) {
      this.userPreferencesService.setPreference(
        USER_PREFERENCE_KEYS.SIDEBAR_EXPANDED,
        event.toString()
      );
    }
  }

  handleHeaderToggle(): void {
    if (this.isMobileView()) {
      this.showMobileSidebar = !this.showMobileSidebar;
    } else {
      this.expandSidebar = !this.expandSidebar;

      // Update the sidebar state service
      this.sidebarStateService.setExpanded(this.expandSidebar);

      // Save the new state for desktop
      this.userPreferencesService.setPreference(
        USER_PREFERENCE_KEYS.SIDEBAR_EXPANDED,
        this.expandSidebar.toString()
      );
    }
  }

  getSidebarClasses(): string {
    const baseClasses = 'transition-transform duration-300 ease-in-out z-50';

    if (this.isMobileView()) {
      // Mobile: fixed positioning with slide animation
      const mobileClasses = 'fixed top-0 left-0 h-full w-64';
      const transformClass = this.showMobileSidebar
        ? 'translate-x-0'
        : '-translate-x-full';
      return `${baseClasses} ${mobileClasses} ${transformClass}`;
    } else {
      // Desktop: normal flow
      return baseClasses;
    }
  }

  closeMobileSidebar(): void {
    this.showMobileSidebar = false;
  }

  onMainContentClick(): void {
    if (this.isMobileView() && this.showMobileSidebar) {
      this.showMobileSidebar = false;
    }
  }

  isMobileView(): boolean {
    return window.innerWidth < 1024; // lg breakpoint in Tailwind
  }

  retryLoading(): void {
    this.loadingError = null;
    this.ngOnInit();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    // Close mobile sidebar when switching to desktop view
    if (!this.isMobileView() && this.showMobileSidebar) {
      this.showMobileSidebar = false;
    }
  }
}
